import React from "react";
import { Box, Container, Heading, Text, VStack, Icon, Tabs, TabList, TabPanels, Tab, TabPanel, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon } from "@chakra-ui/react";
import { FaQuestionCircle } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const PropertyFAQ: React.FC = () => {
  const categories = {
    "Retirement Planning": [
      { q: "Can property really help if my super won't be enough?", a: "Property investment creates an additional income stream outside super. Many of our clients use rental income to supplement super drawdowns, giving them more flexibility and security in retirement." },
      { q: "How does property compare to adding more to super?", a: "Both have benefits. Super offers tax advantages but is locked until preservation age. Property provides accessible equity, rental income before retirement, and significant tax benefits now—while also building long-term wealth." },
      { q: "I'm 45—is it too late to start investing in property?", a: "Many of our most successful clients start in their 40s and 50s. With 15-20 years until retirement, there's ample time to build a meaningful property portfolio that complements your super." }
    ],
    "Tax Benefits": [
      { q: "How much tax can I actually save through property?", a: "High-income earners typically see significant tax reductions through negative gearing, depreciation, and deductible expenses. The exact amount depends on your income, property type, and loan structure—we'll show you the numbers in your consultation." },
      { q: "What expenses can I claim on an investment property?", a: "Loan interest, property management fees, council rates, insurance, repairs, maintenance, depreciation on the building and fixtures, and many more. A depreciation schedule alone can unlock $5,000-$15,000+ in deductions per year for newer properties." },
      { q: "Is negative gearing still worthwhile?", a: "For high-income earners paying top marginal tax rates, negative gearing remains a powerful strategy. The tax refunds effectively subsidise your wealth-building, and many properties move to positive cash flow within a few years." }
    ],
    "Getting Started": [
      { q: "What deposit and income do I need?", a: "Typically 10-20% deposit plus costs. With a combined household income of $200k+, most clients have strong borrowing capacity—often enough for their first or second investment property." },
      { q: "Should I pay off my home first?", a: "Not necessarily. Using equity in your home to invest can accelerate wealth building significantly. The key is structuring loans correctly to maximise tax deductions while managing risk appropriately." },
      { q: "How do I know if I'm ready to invest?", a: "If you have stable income, manageable debt (under 4x income), and capacity to cover holding costs, you're likely ready. Our consultation assesses your position and shows you what's possible." }
    ],
    "Solar Value-Add": [
      { q: "Does solar actually increase property value?", a: "Yes, typically 3-5% increase plus higher rental yields. Tenants actively seek low-energy-cost homes, and solar provides ongoing depreciation benefits for you as the investor." },
      { q: "Can I claim tax deductions on solar?", a: "Solar systems are depreciable assets. You can claim depreciation over the system's effective life, plus potentially access small business instant asset write-offs depending on how the property is structured." },
      { q: "Is solar worth it for investment properties?", a: "Yes. Solar adds to rental yield, property value, and available tax deductions simultaneously. The combined benefit across these three factors is often significant — we'll model the specific numbers for your property in a consultation." }
    ]
  };

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/faq"
        fallbackTitle="Property Investment FAQ"
        fallbackDescription="Frequently asked questions about property investment"
      />
      <NavbarWithCallToAction />

      <Box bgGradient="linear(to-r, cyan.600, blue.600)" color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaQuestionCircle} boxSize={16} />
            <Heading size="3xl" fontWeight="black">Your Questions Answered</Heading>
            <Text fontSize="xl" maxW="3xl">Common questions from high-income earners exploring property investment for retirement and tax minimisation</Text>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={16}>
        <Tabs variant="soft-rounded" colorScheme="blue" size="lg">
          <TabList justifyContent="center" mb={12} flexWrap="wrap" gap={2}>
            {Object.keys(categories).map((cat) => (
              <Tab key={cat}>{cat}</Tab>
            ))}
          </TabList>

          <TabPanels>
            {Object.entries(categories).map(([category, faqs]) => (
              <TabPanel key={category}>
                <Accordion allowMultiple>
                  {faqs.map((faq, i) => (
                    <AccordionItem key={i} border="1px" borderColor="gray.200" borderRadius="lg" mb={4}>
                      <AccordionButton py={6} _expanded={{ bg: "blue.50" }}>
                        <Box flex={1} textAlign="left">
                          <Heading size="md">{faq.q}</Heading>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={6}>
                        <Text color="gray.700">{faq.a}</Text>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Container>

      <Box bg="blue.600" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl">Ready to Get Clear Answers for Your Situation?</Heading>
            <Text fontSize="lg" maxW="2xl">Every investor's circumstances are different. Book a consultation and we'll show you exactly how property investment could work for your retirement planning and tax position.</Text>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyFAQ;
